import { CJAtelier } from 'types/cj';
import { EleveGroup } from 'types/eleveGroups';
import { Eleve } from 'types/eleves';
import { v4 as uuid } from 'uuid';

/**
 * Updates an ephemeral group - either in pendingEphemeralGroups or in database
 */
export function updateEphemeralGroup(
  groupId: string,
  selectedIds: string[],
  groups: EleveGroup[],
  pendingEphemeralGroups: Array<{ id: string; elevesIds: string[] }>,
  eleves: Eleve[],
  onUpdateGroup?: (g: EleveGroup) => void | Promise<any>,
  onPendingEphemeralGroupsChange?: (
    groups: Array<{ id: string; elevesIds: string[] }>,
  ) => void,
) {
  // First check if this is a pending ephemeral group
  const isPending = pendingEphemeralGroups.some(peg => peg.id === groupId);

  if (isPending) {
    // Update pending ephemeral group (not saved yet)
    const updatedPendingGroups = pendingEphemeralGroups.map(g =>
      g.id === groupId ? { ...g, elevesIds: selectedIds } : g,
    );
    onPendingEphemeralGroupsChange?.(updatedPendingGroups);
    return;
  }

  // Must be a saved ephemeral group - update in database
  const savedGroup = groups.find(g => g.id === groupId && g.isEphemeral);
  if (savedGroup) {
    const updatedEleves = eleves
      .filter(e => selectedIds.includes(e.id))
      .map(e => ({
        id: e.id,
        givenName: e.givenName,
        familyName: e.familyName,
        picture: e.picture,
      }));
    onUpdateGroup?.({
      ...savedGroup,
      eleves: updatedEleves,
    });
  }
}

export function handleEphemeralGroupSubmit(
  selectedIds: string[],
  editingGroupId: string | undefined,
  groups: EleveGroup[],
  pendingEphemeralGroups: Array<{ id: string; elevesIds: string[] }>,
  eleves: Eleve[],
  onUpdateGroup?: (g: EleveGroup) => void | Promise<any>,
  onPendingEphemeralGroupsChange?: (
    groups: Array<{ id: string; elevesIds: string[] }>,
  ) => void,
): string | undefined {
  if (editingGroupId) {
    // Use the updateEphemeralGroup function for updating
    updateEphemeralGroup(
      editingGroupId,
      selectedIds,
      groups,
      pendingEphemeralGroups,
      eleves,
      onUpdateGroup,
      onPendingEphemeralGroupsChange,
    );
    return editingGroupId;
  }

  // Create new ephemeral group (pending until save)
  const ephemeralGroupId = uuid();
  const newPendingGroups = [
    ...pendingEphemeralGroups,
    {
      id: ephemeralGroupId,
      elevesIds: selectedIds,
    },
  ];
  onPendingEphemeralGroupsChange?.(newPendingGroups);
  return ephemeralGroupId;
}

export async function handleGroupCreate(
  atelier: CJAtelier,
  group: EleveGroup,
  onChange: (atelier: CJAtelier) => void,
  onCreateGroup?: (g: EleveGroup) => Promise<any>,
) {
  const createdGroup = await onCreateGroup?.(group);
  if (!createdGroup) {
    return createdGroup;
  }

  // Auto-select the newly created group
  const updatedGroupPivots = [...(atelier?.groupPivots || [])];
  if (!updatedGroupPivots.find(gp => gp.id === createdGroup.id)) {
    updatedGroupPivots.push({
      id: createdGroup.id,
      position: 1,
    });
  }

  const newAtelier = {
    ...atelier,
    groupPivots: updatedGroupPivots,
    pendingEphemeralGroups: atelier?.pendingEphemeralGroups,
  };
  onChange(newAtelier);

  return createdGroup;
}

export function handleGroupChange(
  atelier: CJAtelier,
  groupIds: string[],
  onChange: (atelier: CJAtelier) => void,
) {
  // Filter out any pendingEphemeralGroups that are no longer selected
  const filteredPendingGroups = (atelier?.pendingEphemeralGroups || []).filter(
    peg => groupIds.includes(peg.id),
  );

  const newAtelier = {
    ...atelier,
    groupPivots: groupIds.map(id => ({
      id,
      position: atelier.groupPivots?.find(gp => gp.id === id)?.position || 1,
    })),
    // Only keep pendingEphemeralGroups that are still selected
    pendingEphemeralGroups: filteredPendingGroups,
  };
  onChange(newAtelier);
}

export function handleEphemeralGroupsChange(
  atelier: CJAtelier,
  pendingGroups: Array<{ id: string; elevesIds: string[] }>,
  onChange: (atelier: CJAtelier) => void,
) {
  // Find the newly added ephemeral group (if any)
  const existingIds = atelier?.pendingEphemeralGroups?.map(g => g.id) || [];
  const newGroupIds = pendingGroups
    .map(g => g.id)
    .filter(id => !existingIds.includes(id));

  // Add new ephemeral groups to groupPivots for auto-selection
  const updatedGroupPivots = [...(atelier?.groupPivots || [])];
  newGroupIds.forEach(id => {
    if (!updatedGroupPivots.find(gp => gp.id === id)) {
      updatedGroupPivots.push({ id, position: 1 });
    }
  });

  onChange({
    ...atelier,
    pendingEphemeralGroups: pendingGroups,
    groupPivots: updatedGroupPivots,
  });
}
